<script setup lang="ts">
import { useSearchResultsRandomizer } from '~/composables/useSearchResultsRandomizer'
import { useDebounce } from '@vueuse/core'
import { LucideSearch, LucideNewspaper, LucideCircleX, SlidersVertical } from 'lucide-vue-next'
import { FeaturePagination } from '~/components/feature/pagination'
import type { IResponseData, IMultiselectOption } from '~/types'
import { Input } from '~/components/ui/input'
import { Button } from '~/components/ui/button'
import { Alert, AlertTitle, AlertDescription } from '~/components/ui/alert'
import { useCreditFilters } from '~/components/widget/credits/list-meili-search/mocks'
import { EntityDepositItem, EntityDepositSkeleton } from '~/components/entity/deposit'
import type { IDepositeTypes } from '~/components/widget/select-deposit-type'
import type { IDepositsQueryParams } from '~/components/entity/deposit'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import {
  depositOperations,
  percentPayments
} from '~/components/widget/deposits/list-meili-search/mocks'
import {
  buildCurrencyFilter,
  buildDepositsSort,
  buildPeriodFilter
} from '~/components/widget/deposits/list-meili-search/filters'
import { AmountCurrencySelector } from '~/components/feature/amount-currency-selector'
import type { IOrganization } from '~/components/entity/organization/types'

const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()

// The path whose filters are currently loaded. Filter watchers may fire while a
// route change is in flight; writing the URL then would replace the incoming
// path with this one and bounce the user back to the previous section.
const syncedPath = ref('')
const { locale } = useI18n()
const filtersStore = useDepositsFiltersStore()
const { shuffleHighlightedAfterMount } = useSearchResultsRandomizer()

// Track search query state for deposits
const searchQueryState = useState<string>('searchQuery_deposits', () => '')

const { periodOptions } = useCreditFilters()

const props = withDefaults(
  defineProps<{
    queryParams?: IDepositsQueryParams
    organization?: IOrganization
  }>(),
  {
    queryParams: () => ({
      per_page: '10',
      q: ''
    }),
    organization: undefined
  }
)

const { t } = useI18n()

const useLocalFilters = computed(() => !!props.organization)

const hitsPerPage = Number(route.query.per_page || props.queryParams.per_page || 10)

const localSearchQueryString = ref(String(route.query.q || ''))
const localDepositAmountRaw = ref<number | undefined>(undefined)
const localSelectedPeriod = ref<IMultiselectOption[]>([])
const localSelectedGuaranteeRequiredType = ref<string | undefined>(undefined)
const localSelectedTypes = ref<IMultiselectOption[]>([])
const localCurrency = ref<string>('any')
const localSelectedOperations = ref<string[]>([])
const localSelectedPercentPayments = ref<string[]>([])
const localCurrentPage = ref(Number(route.query.page) || 1)

const searchQueryString = computed({
  get: () =>
    useLocalFilters.value ? localSearchQueryString.value : filtersStore.searchQueryString,
  set: (value: string) => {
    if (useLocalFilters.value) {
      localSearchQueryString.value = value
    } else {
      filtersStore.searchQueryString = value
    }
  }
})

// Watch for search query state changes
watch(
  () => searchQueryState.value,
  (newValue) => {
    // Apply value only if it's not empty, has changed, and differs from current search value
    if (newValue && newValue !== searchQueryString.value) {
      searchQueryString.value = newValue
      // Clear state after applying
      searchQueryState.value = ''
    }
  },
  { immediate: true }
)

const depositAmountRaw = computed({
  get: () => (useLocalFilters.value ? localDepositAmountRaw.value : filtersStore.depositAmountRaw),
  set: (value: number | undefined) => {
    if (useLocalFilters.value) {
      localDepositAmountRaw.value = value
    } else {
      filtersStore.depositAmountRaw = value
    }
  }
})

const currency = computed({
  get: () => (useLocalFilters.value ? localCurrency.value : filtersStore.currency),
  set: (value: string) => {
    if (useLocalFilters.value) {
      localCurrency.value = value
    } else {
      filtersStore.currency = value
    }
  }
})

const selectedPeriod = computed({
  get: () => (useLocalFilters.value ? localSelectedPeriod.value : filtersStore.selectedPeriod),
  set: (value: IMultiselectOption[]) => {
    if (useLocalFilters.value) {
      localSelectedPeriod.value = value
    } else {
      filtersStore.selectedPeriod = value
    }
  }
})

const selectedGuaranteeRequiredType = computed({
  get: () =>
    useLocalFilters.value
      ? localSelectedGuaranteeRequiredType.value
      : filtersStore.selectedGuaranteeRequiredType,
  set: (value: string | undefined) => {
    if (useLocalFilters.value) {
      localSelectedGuaranteeRequiredType.value = value
    } else {
      filtersStore.selectedGuaranteeRequiredType = value
    }
  }
})

const selectedTypes = computed({
  get: () => (useLocalFilters.value ? localSelectedTypes.value : filtersStore.selectedTypes),
  set: (value: IMultiselectOption[]) => {
    if (useLocalFilters.value) {
      localSelectedTypes.value = value
    } else {
      filtersStore.selectedTypes = value
    }
  }
})

const selectedOperations = computed({
  get: () =>
    useLocalFilters.value ? localSelectedOperations.value : filtersStore.selectedOperations,
  set: (value: string[]) => {
    if (useLocalFilters.value) {
      localSelectedOperations.value = value
    } else {
      filtersStore.selectedOperations = value
    }
  }
})

const selectedPercentPayments = computed({
  get: () =>
    useLocalFilters.value
      ? localSelectedPercentPayments.value
      : filtersStore.selectedPercentPayments,
  set: (value: string[]) => {
    if (useLocalFilters.value) {
      localSelectedPercentPayments.value = value
    } else {
      filtersStore.selectedPercentPayments = value
    }
  }
})

const currentPage = computed({
  get: () => (useLocalFilters.value ? localCurrentPage.value : filtersStore.currentPage),
  set: (value: number) => {
    if (useLocalFilters.value) {
      localCurrentPage.value = value
    } else {
      filtersStore.currentPage = value
    }
  }
})

const debouncedQuery = useDebounce(searchQueryString, 500)
const depositAmount = useDebounce(depositAmountRaw, 500)

const { data: depositTypesData } = await useApi<IResponseData<IDepositeTypes[]>>(
  () => '/deposit_types',
  {
    key: 'deposit-types-list-meili-search'
  }
)

const typesOptions = computed<IMultiselectOption[]>(
  () =>
    depositTypesData.value?.data.map((item) => ({
      id: item.id,
      label: item.title,
      value: item.slug
    })) ?? []
)

const menuItems = ['KGS', 'RUB', 'USD', 'EUR', 'KZT', 'CNY']
const currencyList = useCurrencyStore().list
const currencyOptions = computed(() => [
  {
    id: 0,
    label: t('common.any_currency'),
    value: 'any'
  },
  ...currencyList
    .filter((c) => menuItems.includes(c.code))
    .map((c, i) => ({
      id: i + 1,
      label: c.code,
      value: c.code.toLowerCase()
    }))
])

const operationsFilters = computed(() => {
  if (!selectedOperations.value.length) return ''
  return '(' + selectedOperations.value.map((o) => `${o} = true`).join(' OR ') + ')'
})

const percentPaymentsFilters = computed(() => {
  if (!selectedPercentPayments.value.length) return ''

  const payoutCycleValues: string[] = []
  const otherFilters: string[] = []

  selectedPercentPayments.value.forEach((o) => {
    if (o === 'monthly' || o === 'end_of_term') {
      if (!payoutCycleValues.includes('monthly_end_of_term')) {
        payoutCycleValues.push('monthly_end_of_term')
      }
      payoutCycleValues.push(o)
    }
  })

  const payoutCycleFilters = payoutCycleValues.map((val) => `payout_cycle = ${val}`)

  const combinedFilters = [...payoutCycleFilters, ...otherFilters]

  return combinedFilters.length ? `(${combinedFilters.join(' OR ')})` : ''
})

const activeFilter = computed(() => {
  const allFilters: string[] = []

  if (props.organization) {
    allFilters.push(`organizations.id = ${props.organization.id}`)
    allFilters.push(`organizations.organization_type = '${props.organization.organization_type}'`)
  }

  if (depositAmount.value && currency.value && currency.value !== 'any') {
    const amount = depositAmount.value
    const baseCondition = `(settings.min_amount_${currency.value} <= ${amount} AND (settings.max_amount_${currency.value} >= ${amount} OR settings.max_amount_${currency.value} = 0))`

    allFilters.push(`(${baseCondition})`)
  }

  if (selectedPercentPayments.value.includes('progressive_accrual')) {
    allFilters.push('progressive_accrual = true')
  }

  if (operationsFilters.value) {
    allFilters.push(operationsFilters.value)
  }

  if (percentPaymentsFilters.value) {
    allFilters.push(percentPaymentsFilters.value)
  }

  if (selectedPeriod.value.length) {
    const hasCurrency = currency.value && currency.value !== 'any'
    const currencyId = hasCurrency
      ? currencyList.find((el) => el.code.toLowerCase() === currency.value)?.id
      : undefined

    const periodFilters = selectedPeriod.value.map((periodOption) =>
      buildPeriodFilter(periodOption.value, currencyId)
    )

    allFilters.push(`(${periodFilters.join(' OR ')})`)
  }

  if (selectedGuaranteeRequiredType.value && selectedGuaranteeRequiredType.value !== 'any') {
    allFilters.push(`income_confirmation = "${selectedGuaranteeRequiredType.value === '1'}"`)
  }

  if (currency.value && currency.value !== 'any') {
    allFilters.push(buildCurrencyFilter(currency.value))
  }

  if (selectedTypes.value?.length) {
    const typesFilter = selectedTypes.value.map((o) => `types.id = ${o.id}`).join(' OR ')
    allFilters.push(`(${typesFilter})`)
  }

  return allFilters.length ? [`(${allFilters.join(' AND ')})`] : undefined
})

// Period and currency live in a shared store, so the URL is their single source of
// truth: an URL without them clears the leftover selection from a previous page
// instead of silently keeping it.
const periodFromUrl = () =>
  periodOptions.find((option) => option.value === String(route.query.period ?? ''))

const currencyFromUrl = () =>
  currencyOptions.value.find((option) => option.value === String(route.query.currency ?? ''))
    ?.value ?? 'any'

// The deposit type comes from the path (/deposits/currency), not the query.
const typeSlugFromPath = () =>
  (Array.isArray(route.params.type) ? route.params.type[0] : route.params.type) || ''

const typeFromUrl = () => {
  const slug = typeSlugFromPath()

  if (!slug) return undefined

  const type = typesOptions.value.find((item) => item.value === slug)

  return type ? [type] : undefined
}

const initializeFiltersFromUrl = () => {
  if (route.query.q) {
    searchQueryString.value = String(route.query.q)
  }

  if (route.query.page) {
    currentPage.value = Number(route.query.page)
  }

  const period = periodFromUrl()
  selectedPeriod.value = period ? [period] : []
  currency.value = currencyFromUrl()

  if (typesOptions.value.length) {
    selectedTypes.value = typeFromUrl() ?? []
  }

  syncedPath.value = route.path
}

initializeFiltersFromUrl()

const { search } = useMeiliSearch(`${config.public.meiliEnvironment}_deposits`)

// When a specific currency is selected, show and sort by that currency's values
const activeCurrency = computed(() =>
  currency.value && currency.value !== 'any' ? currency.value : undefined
)

const performSearch = async () => {
  const page = currentPage.value
  const filter = activeFilter.value

  try {
    const searchResult = await search(searchQueryString.value, {
      hitsPerPage,
      page,
      sort: buildDepositsSort(activeCurrency.value),
      attributesToSearchOn: [
        'title.ky',
        'title.ru',
        'organization.title.ky',
        'organization.title.ru',
        'organization.official_title.ky',
        'organization.official_title.ru',
        'organization.slug',
        'types.title.ky',
        'types.title.ru'
      ],
      attributesToHighlight: ['title'],
      ...(filter ? { filter } : {})
    })

    return searchResult
  } catch (e) {
    console.error('MeiliSearch fetch error:', e)
    return null
  }
}

const {
  data: result,
  pending: isSearching,
  error: fetchError,
  refresh: refreshDeposits
} = await useAsyncData(`deposits-search-${props.organization?.id || 'all'}`, performSearch)

// All deposit types share one route, so switching between them reuses this
// component and the type has to be re-read instead of staying at its mount value.
// Only an actual section change may do so: comparing against the previous slug
// keeps a type the user cleared by hand from coming back on the next filter
// change, which also touches the URL. The refresh deliberately skips updateUrl —
// writing the URL mid-navigation would cancel the pending route change.
watch(
  () => typeSlugFromPath(),
  (slug, previousSlug) => {
    if (slug === previousSlug) return

    selectedTypes.value = typeFromUrl() ?? []
    refreshDeposits()
  }
)

const hasError = computed(() => !!fetchError.value)

const orderedHits = shuffleHighlightedAfterMount(() => result.value?.hits || [])

const typedHits = computed(() => {
  const cur = activeCurrency.value
  const bestRateKey = cur ? `best_rate_${cur}` : 'best_rate'
  const maxPeriodKey = cur ? `max_period_${cur}` : 'max_period'

  return orderedHits.value.map((hit) => ({
    id: hit.id,
    url: hit.url,
    title: hit._formatted?.title[locale.value] || hit.title[locale.value],
    organization: {
      ...hit.organization,
      title: hit.organization.title?.[locale.value] || '',
      official_title: hit.organization.official_title?.[locale.value] || ''
    },
    max_period: hit.settings?.[maxPeriodKey]?.[locale.value],
    best_rate: hit.settings?.[bestRateKey]?.[locale.value],
    currencies: hit.currencies,
    for_whom: hit.for_whom,
    types: hit.types.map((type) => ({
      ...type,
      title: type.title[locale.value]
    })),
    highlight: hit.highlight,
    ratings: hit.ratings ? hit.ratings.average_rate : null
  }))
})

const updateUrl = () => {
  // Debounced filters can fire after the user already left for another deposit
  // type. `route` still holds the path this component was rendered for, so the
  // live one is read from the router to avoid replacing it with the stale path.
  const current = router.currentRoute.value

  if (current.path !== syncedPath.value) return

  // Keep the period in the URL so the search alias links reflect the active filter
  const singleSelectedPeriod =
    selectedPeriod.value.length === 1 ? String(selectedPeriod.value[0].value) : undefined

  const selectedCurrency =
    currency.value && currency.value !== 'any' ? String(currency.value) : undefined

  router.replace({
    path: current.path,
    query: {
      ...current.query,
      q: debouncedQuery.value || undefined,
      period: singleSelectedPeriod,
      currency: selectedCurrency,
      page: currentPage.value > 1 ? String(currentPage.value) : undefined
    }
  })
}

// Narrowing the results can leave the current page out of range, so any filter
// change restarts from the first page. Paging itself must not reset it.
watch(
  [
    debouncedQuery,
    selectedPeriod,
    selectedGuaranteeRequiredType,
    depositAmount,
    selectedOperations,
    selectedPercentPayments,
    currency
  ],
  () => {
    if (isOpen.value) return

    // Resetting the page triggers the watcher below, which does the refresh.
    if (currentPage.value !== 1) {
      currentPage.value = 1
      return
    }

    updateUrl()
    refreshDeposits()
  }
)

watch(currentPage, () => {
  if (isOpen.value) return
  updateUrl()
  refreshDeposits()
})

// Search alias links change only the URL, so these filters are re-applied from it
watch(
  () => [route.path, route.query.period, route.query.currency],
  () => {
    // Declared last on purpose: by the time this runs every other watcher has
    // reacted to the navigation, so the state matches the path and writing the
    // URL is safe again.
    syncedPath.value = route.path

    const period = periodFromUrl()
    const nextPeriod = period?.value ?? ''
    const nextCurrency = currencyFromUrl()

    const currentPeriod = selectedPeriod.value.length === 1 ? selectedPeriod.value[0].value : ''
    const hasChanged = nextPeriod !== currentPeriod || nextCurrency !== currency.value

    if (!hasChanged) return

    // Assigning these lands in the filter watcher above, which resets the page.
    selectedPeriod.value = period ? [period] : []
    currency.value = nextCurrency
  }
)

const changePage = (page: number) => {
  currentPage.value = page
}

const isOpen = ref(false)

const toggle = () => {
  isOpen.value = !isOpen.value
}

const handleCheckboxChange = (
  value: string,
  checked: boolean,
  targetArray: 'selectedOperations' | 'selectedPercentPayments'
) => {
  if (targetArray === 'selectedOperations') {
    const operations = [...selectedOperations.value]
    if (checked) {
      if (!operations.includes(value)) {
        operations.push(value)
      }
    } else {
      const index = operations.indexOf(value)
      if (index > -1) {
        operations.splice(index, 1)
      }
    }
    selectedOperations.value = operations
  }

  if (targetArray === 'selectedPercentPayments') {
    const payments = [...selectedPercentPayments.value]
    if (checked) {
      if (!payments.includes(value)) {
        payments.push(value)
      }
    } else {
      const index = payments.indexOf(value)
      if (index > -1) {
        payments.splice(index, 1)
      }
    }
    selectedPercentPayments.value = payments
  }
}

const resetFilters = () => {
  if (useLocalFilters.value) {
    localSearchQueryString.value = ''
    localDepositAmountRaw.value = undefined
    localCurrency.value = 'any'
    localSelectedPeriod.value = []
    localSelectedGuaranteeRequiredType.value = undefined
    localSelectedTypes.value = []
    localSelectedOperations.value = []
    localSelectedPercentPayments.value = []
    localCurrentPage.value = 1
  } else {
    filtersStore.reset()
  }
  // Clear state on reset
  searchQueryState.value = ''
}

const applyFilters = () => {
  isOpen.value = false
  updateUrl()
  refreshDeposits()
}

const clearSearch = () => {
  if (useLocalFilters.value) {
    localSearchQueryString.value = ''
  } else {
    filtersStore.searchQueryString = ''
  }
  if (searchQueryState) {
    searchQueryState.value = ''
  }
}
</script>

<template>
  <div class="space-y-5">
    <div class="relative">
      <Input
        v-model="searchQueryString"
        :placeholder="$t('common.search_organization_placeholder')"
        class="h-[52px] pl-14 pr-14"
      />
      <LucideSearch class="absolute left-4 top-3.5 size-6 text-gray-600" />
      <Button
        v-if="searchQueryString"
        size="icon"
        variant="link"
        class="absolute right-4 top-3.5 size-6 text-red-400 hover:text-red-400"
        @click="clearSearch"
      >
        <LucideCircleX />
      </Button>
    </div>

    <div class="flex items-start justify-between gap-2">
      <div class="grid grid-cols-1 gap-2 md:grid-cols-3">
        <AmountCurrencySelector
          :amount="depositAmountRaw"
          :currency="currency"
          :currency-options="currencyOptions"
          height="h-[52px]"
          @update:amount="depositAmountRaw = $event"
          @update:currency="currency = $event"
        />

        <MultiSelect
          v-model="selectedPeriod"
          class="multiselect_height_52"
          :options="periodOptions"
          :placeholder="$t('common.period_placeholder')"
        />
        <MultiSelect
          v-model="selectedTypes"
          :options="typesOptions"
          :placeholder="$t('widget.deposits.list_meili_search.types_placeholder')"
          class="multiselect_height_52"
          @update:model-value="refreshDeposits()"
        />
      </div>
      <Button
        variant="blue"
        size="icon"
        class="mt-0.5 !p-6"
        :aria-label="$t('common.all_filters')"
        @click="toggle"
      >
        <SlidersVertical class="h-6 w-6" />
      </Button>
    </div>

    <section v-if="!isSearching && !hasError && result" aria-labelledby="deposits">
      <div v-if="result.hits?.length" class="CardsList">
        <Heading id="deposits" tag="h2" class="sr-only">{{ $t('common.deposits') }}</Heading>
        <EntityDepositItem
          v-for="item in typedHits"
          :key="item.id"
          :item="item"
          :hide-organization-details="!!organization"
          :selected-currency="activeCurrency"
        />

        <div class="mt-7 flex justify-end">
          <FeaturePagination
            :meta="{
              current_page: result.page || 1,
              total: result.totalHits || 0,
              per_page: result.hitsPerPage || 0,
              last_page: result.totalPages || 0
            }"
            :links="{
              prev:
                currentPage > 1
                  ? `?q=${searchQueryString}&page=${result.page ? result.page - 1 : 1}`
                  : null,
              next:
                currentPage < (result.totalPages || 1)
                  ? `?q=${searchQueryString}&page=${result.page ? result.page + 1 : 1}`
                  : null
            }"
            @change="changePage"
          />
        </div>
      </div>

      <Alert v-else variant="blue">
        <LucideNewspaper />
        <AlertDescription>{{ $t('common.nothing_found') }}</AlertDescription>
      </Alert>
    </section>

    <section v-else-if="isSearching">
      <EntityDepositSkeleton :count="hitsPerPage" :hide-organization-details="!!organization" />
    </section>

    <section v-else-if="hasError">
      <Alert variant="yellow">
        <AlertTitle>{{ $t('common.error_loading_title') }}</AlertTitle>
        <AlertDescription>{{ $t('common.error_loading_description') }}</AlertDescription>
      </Alert>
    </section>

    <Dialog v-model:open="isOpen">
      <DialogContent overlay-class="bg-gray-600/50" class="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{{ $t('common.all_filters') }}</DialogTitle>
        </DialogHeader>
        <div class="flex flex-col gap-5">
          <AmountCurrencySelector
            :amount="depositAmountRaw"
            :currency="currency"
            :currency-options="currencyOptions"
            height="h-[52px]"
            @update:amount="depositAmountRaw = $event"
            @update:currency="currency = $event"
          />
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <MultiSelect
              v-model="selectedPeriod"
              :options="periodOptions"
              :placeholder="$t('common.period_placeholder')"
              class="multiselect_height_52"
            />

            <MultiSelect
              v-model="selectedTypes"
              :options="typesOptions"
              :placeholder="$t('widget.deposits.list_meili_search.types_placeholder')"
              class="multiselect_height_52"
            />
          </div>
          <div class="grid grid-cols-2 gap-2">
            <div class="flex flex-col gap-2">
              <Heading tag="h6">{{
                $t('widget.deposits.list_meili_search.operations_heading')
              }}</Heading>
              <div
                v-for="item in depositOperations"
                :key="item.value"
                class="flex items-center gap-2"
              >
                <Checkbox
                  :id="item.value"
                  :value="item.value"
                  :model-value="selectedOperations.includes(item.value)"
                  @update:model-value="
                    (checked) => handleCheckboxChange(item.value, checked, 'selectedOperations')
                  "
                />
                <Label :for="item.value">{{ $t(item.label) }}</Label>
              </div>
            </div>

            <div class="flex flex-col gap-2">
              <Heading tag="h6">{{
                $t('widget.deposits.list_meili_search.percent_payments_heading')
              }}</Heading>
              <div
                v-for="item in percentPayments"
                :key="item.value"
                class="flex items-center gap-2"
              >
                <Checkbox
                  :id="item.value"
                  :value="item.value"
                  :model-value="selectedPercentPayments.includes(item.value)"
                  @update:model-value="
                    (checked) =>
                      handleCheckboxChange(item.value, checked, 'selectedPercentPayments')
                  "
                />
                <Label :for="item.value">{{ $t(item.label) }}</Label>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter class="gap-2">
          <Button variant="outline-blue" @click="resetFilters">{{
            $t('common.reset_filters')
          }}</Button>
          <Button variant="blue" @click="applyFilters">{{ $t('common.show') }}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
